import * as React from 'react';
import { useState } from 'react';

import copy from 'copy-to-clipboard';

import { Alert, AlertHeading, AlertMessage, Button, RadioGroup, Text } from '@components/common';
import { SlideOut } from '@components/common/SlideOut';
import {
  ADD_EXISTING,
  ADD_MANUAL,
  INVITE_VIA_LINK,
  StudyAction
} from '@components/StudiesApp/components/StudyPublished/components/ACTIONS';
import { ZDS } from '@components/StudiesApp/components/StudyPublished/components/ZDS';
import { LinkSVG } from '@components/svgs';
import { track } from '@components/tracking';
import { useToaster } from '@stores/toaster';

type RecruitmentMethod = 'share_link' | 'recruit_externally' | 'recruit_panel';

interface Props {
  onClose: () => void;
  onPublish: () => void;
  study?: {
    slug: string;
    participant_limit: number | null;
  };
}

const RECRUITMENT_OPTIONS = [
  {
    label: 'Share a link',
    helper: 'Generic participant invitation links that anyone can access.',
    value: 'share_link' as const
  },
  {
    label: 'Recruit externally',
    helper: "Access Respondent's 3m+ panel of qualified B2B and B2C participants.",
    value: 'recruit_externally' as const
  },
  {
    label: 'Recruit from your panel',
    helper: 'Browse your database, import a list, or add someone manually.',
    value: 'recruit_panel' as const
  }
];

export const PublishSlideout: React.FC<Props> = ({ onClose, onPublish, study }) => {
  const showToast = useToaster();
  const [selectedMethod, setSelectedMethod] = useState<RecruitmentMethod>('share_link');
  const participantCount = study?.participant_limit || 0;
  const feePerParticipant = 35;
  const totalAmount = participantCount * feePerParticipant;
  const studyUrl = `${window.location.origin}/studies/${study?.slug}`;

  const handleCopyLink = () => {
    copy(studyUrl);
    showToast({
      heading: 'Success!',
      text: 'Link copied to clipboard',
      icon: 'success'
    });
    track('copied_study_link', { studyUrl });
  };

  const handleMethodChange = (value: string) => {
    setSelectedMethod(value as RecruitmentMethod);
  };

  const renderContent = () => {
    switch (selectedMethod) {
      case 'share_link':
        return (
          <div className='mb-6'>
            <Text bold h='500' mb='4'>
              Copy test link to share
            </Text>
            <div className='flex items-center space-x-2 rounded-md border border-gray-200 bg-gray-100 px-4 py-2.5'>
              <Text truncate className='flex-1' h='400'>
                {studyUrl}
              </Text>
              <button
                onClick={handleCopyLink}
                className='focus:outline-none rounded-full bg-white p-1 hover:bg-indigo-100 hover:text-indigo-600'
              >
                <LinkSVG />
              </button>
            </div>
          </div>
        );
      case 'recruit_externally':
        return (
          <>
            <Text bold h='500' mb='4'>
              External recruitment summary
            </Text>
            <div className='mb-6'>
              <Text bold mb='2' h='400'>
                Order summary
              </Text>
              <div className='space-y-2'>
                <div className='flex justify-between'>
                  <Text h='400'>Participants ({participantCount})</Text>
                  <Text h='400'>${participantCount * feePerParticipant}</Text>
                </div>
                <div className='flex justify-between'>
                  <Text h='400'>Fees per participant</Text>
                  <Text h='400'>${feePerParticipant}</Text>
                </div>
              </div>
            </div>

            <div className='mb-6'>
              <Text bold mb='2' h='400'>
                Amount due
              </Text>
              <Text h='400'>${totalAmount}</Text>
            </div>

            <Alert type='info'>
              <AlertHeading>What happens next?</AlertHeading>
              <AlertMessage>
                Upon submission, we will initiate recruitment requests via Respondent to qualified candidates who meet
                your audience criteria.
              </AlertMessage>
            </Alert>
          </>
        );
      case 'recruit_panel':
        return (
          <div className='mb-6'>
            <Text bold h='500' mb='4'>
              How to recruit from your panel
            </Text>
            <ZDS
              setMode={() => {}}
              study={study as any}
              setAddCandidatesDefaultStudyLimit={() => {}}
              studyLimits={[]}
              demoMode
            />
          </div>
        );
    }
  };

  const renderFooter = () => {
    switch (selectedMethod) {
      case 'share_link':
      case 'recruit_panel':
        return (
          <div className='flex justify-end'>
            <Button medium onClick={onClose}>
              Close
            </Button>
          </div>
        );
      case 'recruit_externally':
        return (
          <div className='flex justify-end space-x-4'>
            <Button primary medium onClick={onPublish}>
              Confirm & Recruit for ${totalAmount}
            </Button>
            <Button medium onClick={onClose}>
              Cancel
            </Button>
          </div>
        );
    }
  };

  return (
    <SlideOut
      title='Recruit participants'
      subtitle='Invite participants that meet your audience criteria.'
      onClose={onClose}
      renderFooter={renderFooter}
    >
      <div className='p-6'>
        <Text bold h='500' mb='4'>
          How do you want to recruit participants?
        </Text>
        <div className='mb-6'>
          <RadioGroup options={RECRUITMENT_OPTIONS} selected={selectedMethod} onChange={handleMethodChange} />
        </div>
        <div className='mt-6'>{renderContent()}</div>
      </div>
    </SlideOut>
  );
};
